import React, { useState, useEffect } from "react";
import { Form, DatePicker, InputNumber, Tooltip, Card, Switch, Input, Collapse, Divider, Checkbox, Select } from "antd";
import { useIntl } from "react-intl";
import {
    InfoCircleOutlined,
    CheckCircleOutlined,
    MailOutlined,
    WarningOutlined,
    StarFilled
} from "@ant-design/icons";
import dayjs from "dayjs";
import { ISurveyTheme } from "../../services/nestjs-base-api/types";
import { useGetActiveFinalQuestionsQuery, useGetSurveyConfigQuery } from "../../services/nestjs-base-api";
import { useAuth } from "../../hooks/useAuth";
import { ThankYouPreview } from './ThankYouPreview';

const { TextArea } = Input;
const { Panel } = Collapse;

interface ConfigurationStepProps {
    formData: {
        startsAt?: Date;
        endsAt?: Date;
        questionsPerResponse: number;
        rotationStrategy: 'sequential' | 'random' | 'balanced' | 'adaptive';
        showProgressBar: boolean;
        animationSpeed: number;
        allowSkip: boolean;
        allowBackNavigation: boolean;
        persistProgress: boolean;
        expirationHours: number;
        completionTitle: string;
        completionSubtitle: string;
        completionButtonText: string;
        completionButtonLink?: string;
        showCompletionButton: boolean;
        completionSecondaryButtonText?: string;
        completionSecondaryButtonLink?: string;
        // The reward on the thank-you page (SYR-105 phase 2).
        showReward?: boolean;
        discountCode?: string | null;
        offerDescription?: string | null;
        offerTerms?: string | null;
        showCompletionSecondaryButton: boolean;
        collectEmail: boolean;
        emailRequired: boolean;
        emailPlaceholder: string;
        npsRequired: boolean;
        showWelcomePage: boolean;
        finalQuestionId?: number | null;
        theme?: ISurveyTheme;
    };
    errors: Record<string, string>;
    onChange: (updates: any) => void;
}

const ConfigurationStep: React.FC<ConfigurationStepProps> = ({
    formData,
    errors,
    onChange
}) => {
    const intl = useIntl();
    const { user } = useAuth();
    const [neverExpires, setNeverExpires] = useState(!formData.endsAt);


    // Fetch survey config for fixed statements
    const { data: surveyConfig } = useGetSurveyConfigQuery();
    const fixedStatements = surveyConfig?.fixedStatementsPerResponse || 0;

    // Fetch active final questions
    const { data: finalQuestionsData } = useGetActiveFinalQuestionsQuery({
        jwtToken: user?.jwtToken || '',
    }, {
        skip: !user?.jwtToken
    });

    // Auto-select the default final question for new surveys
    useEffect(() => {
        // Only auto-select if:
        // 1. Data has loaded
        // 2. There is a default final question
        // 3. finalQuestionId is currently undefined (new survey)
        if (
            finalQuestionsData?.defaultFinalQuestionId &&
            formData.finalQuestionId === undefined
        ) {
            onChange({ finalQuestionId: finalQuestionsData.defaultFinalQuestionId });
        }
    }, [finalQuestionsData?.defaultFinalQuestionId, formData.finalQuestionId, onChange]);

    const handleStartDateChange = (date: dayjs.Dayjs | null) => {
        onChange({ startsAt: date ? date.toDate() : undefined });
    };

    const handleEndDateChange = (date: dayjs.Dayjs | null) => {
        onChange({ endsAt: date ? date.toDate() : undefined });
    };

    const handleNeverExpiresChange = (e: any) => {
        const checked = e.target.checked;
        setNeverExpires(checked);
        if (checked) {
            onChange({ endsAt: undefined });
        }
    };

    const handleQuestionsChange = (value: number | null) => {
        onChange({ questionsPerResponse: value || 12 });
    };

    const handleRotationStrategyChange = (e: any) => {
        onChange({ rotationStrategy: e.target.value });
    };

    // Survey Settings handlers
    const handleShowProgressBarChange = (checked: boolean) => {
        onChange({ showProgressBar: checked });
    };

    const handleAnimationSpeedChange = (value: number | null) => {
        onChange({ animationSpeed: value || 800 });
    };

    const handleAllowSkipChange = (checked: boolean) => {
        onChange({ allowSkip: checked });
    };

    const handleAllowBackNavigationChange = (checked: boolean) => {
        onChange({ allowBackNavigation: checked });
    };

    const handlePersistProgressChange = (checked: boolean) => {
        onChange({ persistProgress: checked });
    };

    const handleExpirationHoursChange = (value: number | null) => {
        onChange({ expirationHours: value || 24 });
    };


    // Completion Page handlers
    const handleCompletionTitleChange = (e: any) => {
        onChange({ completionTitle: e.target.value });
    };

    const handleCompletionSubtitleChange = (e: any) => {
        onChange({ completionSubtitle: e.target.value });
    };

    const handleCompletionButtonTextChange = (e: any) => {
        onChange({ completionButtonText: e.target.value });
    };

    const handleCompletionButtonLinkChange = (e: any) => {
        onChange({ completionButtonLink: e.target.value });
    };

    const handleShowCompletionButtonChange = (checked: boolean) => {
        onChange({ showCompletionButton: checked });
    };

    // Secondary completion CTA handlers (SYR-93)
    const handleCompletionSecondaryButtonTextChange = (e: any) => {
        onChange({ completionSecondaryButtonText: e.target.value });
    };

    const handleCompletionSecondaryButtonLinkChange = (e: any) => {
        onChange({ completionSecondaryButtonLink: e.target.value });
    };

    const handleShowRewardChange = (checked: boolean) => {
        // Hides the block without discarding what was typed — show_reward is a
        // stored flag, and the public payload gates on it, so a hidden code is
        // never served to a respondent.
        onChange({ showReward: checked });
    };

    const handleDiscountCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange({ discountCode: e.target.value });
    };

    const handleOfferDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange({ offerDescription: e.target.value });
    };

    const handleOfferTermsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        onChange({ offerTerms: e.target.value });
    };

    const handleShowCompletionSecondaryButtonChange = (checked: boolean) => {
        onChange({ showCompletionSecondaryButton: checked });
    };

    // Email Collection handlers - removed as these are now always enabled

    const handleEmailPlaceholderChange = (e: any) => {
        onChange({ emailPlaceholder: e.target.value });
    };

    // NPS Collection handler
    const handleNpsRequiredChange = (checked: boolean) => {
        onChange({ npsRequired: checked });
    };

    // Final Question handler
    const handleFinalQuestionChange = (value: number | null) => {
        // Pass null explicitly when clearing, so API knows to clear the value
        onChange({ finalQuestionId: value });
    };

    // URL validation helper
    const isValidUrl = (url: string) => {
        if (!url) return true; // Empty is valid (optional field)
        try {
            new URL(url);
            return true;
        } catch {
            return false;
        }
    };

    // Secondary CTA link must be an absolute https URL (SYR-93, mirrors API rule)
    const isValidHttpsUrl = (url: string) => {
        if (!url) return true; // Empty is valid (optional field)
        try {
            return new URL(url).protocol === 'https:';
        } catch {
            return false;
        }
    };

    const rotationStrategies = [
        {
            value: 'balanced',
            label: intl.formatMessage({ id: 'survey.form.rotationStrategy.balanced' }),
            description: intl.formatMessage({ id: 'survey.form.rotationStrategy.balancedDesc' })
        },
        {
            value: 'sequential',
            label: intl.formatMessage({ id: 'survey.form.rotationStrategy.sequential' }),
            description: intl.formatMessage({ id: 'survey.form.rotationStrategy.sequentialDesc' })
        },
        {
            value: 'random',
            label: intl.formatMessage({ id: 'survey.form.rotationStrategy.random' }),
            description: intl.formatMessage({ id: 'survey.form.rotationStrategy.randomDesc' })
        },
        {
            value: 'adaptive',
            label: intl.formatMessage({ id: 'survey.form.rotationStrategy.adaptive' }),
            description: intl.formatMessage({ id: 'survey.form.rotationStrategy.adaptiveDesc' })
        }
    ];

    return (
        <div>
            <h3>{intl.formatMessage({ id: 'survey.create.step2.title' })}</h3>
            <p style={{ color: '#6b7280', marginBottom: 24 }}>
                {intl.formatMessage({ id: 'survey.create.step2.subtitle' })}
            </p>

            <Form layout="vertical">
                {/* Basic Configuration */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
                    <Form.Item
                        label={intl.formatMessage({ id: 'survey.form.startDate' })}
                        validateStatus={errors.startsAt ? 'error' : ''}
                        help={errors.startsAt}
                    >
                        <DatePicker
                            value={formData.startsAt ? dayjs(formData.startsAt) : null}
                            onChange={handleStartDateChange}
                            placeholder={intl.formatMessage({ id: 'survey.form.startDatePlaceholder' })}
                            size="large"
                            style={{ width: '100%' }}
                            disabledDate={(current) => current && current < dayjs().startOf('day')}
                            showTime
                            format="YYYY-MM-DD HH:mm"
                        />
                    </Form.Item>

                    <Form.Item
                        label={intl.formatMessage({ id: 'survey.form.endDate' })}
                        validateStatus={errors.endsAt ? 'error' : ''}
                        help={errors.endsAt}
                    >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <DatePicker
                                value={!neverExpires && formData.endsAt ? dayjs(formData.endsAt) : null}
                                onChange={handleEndDateChange}
                                placeholder={neverExpires ? intl.formatMessage({ id: 'survey.form.endDateNever' }) : intl.formatMessage({ id: 'survey.form.endDatePlaceholder' })}
                                size="large"
                                style={{ width: '100%' }}
                                disabled={neverExpires}
                                disabledDate={(current) => {
                                    if (!formData.startsAt) return current && current < dayjs().startOf('day');
                                    return current && current < dayjs(formData.startsAt).startOf('day');
                                }}
                                showTime
                                format="YYYY-MM-DD HH:mm"
                            />
                            <Checkbox
                                checked={neverExpires}
                                onChange={handleNeverExpiresChange}
                            >
                                {intl.formatMessage({ id: 'survey.form.endDateNever' })}
                            </Checkbox>
                        </div>
                    </Form.Item>
                </div>

                <Form.Item
                    label={
                        <span>
                            {intl.formatMessage({ id: 'survey.form.questionsPerResponse' })}
                            <Tooltip title={intl.formatMessage({ id: 'survey.form.questionsPerResponseTooltip' })}>
                                <InfoCircleOutlined style={{ marginLeft: 4, color: '#6b7280' }} />
                            </Tooltip>
                        </span>
                    }
                    validateStatus={errors.questionsPerResponse ? 'error' : ''}
                    help={errors.questionsPerResponse}
                >
                    <InputNumber
                        value={fixedStatements > 0 ? fixedStatements : formData.questionsPerResponse}
                        onChange={handleQuestionsChange}
                        min={5}
                        max={15}
                        size="large"
                        style={{ width: 200 }}
                        addonAfter={intl.formatMessage({ id: 'survey.form.questions' })}
                        disabled={fixedStatements > 0}
                    />
                    <div style={{ color: '#6b7280', fontSize: '12px', marginTop: 4 }}>
                        {intl.formatMessage({ id: fixedStatements > 0 ? 'survey.form.questionsFixedHelp' : 'survey.form.questionsRangeHelp' })}
                    </div>
                </Form.Item>

                {/* Question Rotation Strategy - Hidden for now */}
                {/* <Form.Item
                    label={intl.formatMessage({ id: 'survey.form.rotationStrategy.label' })}
                    style={{ marginBottom: 32 }}
                >
                    <Radio.Group
                        value={formData.rotationStrategy}
                        onChange={handleRotationStrategyChange}
                        style={{ width: '100%' }}
                    >
                        <div style={{ display: 'grid', gap: 16 }}>
                            {rotationStrategies.map(strategy => (
                                <Card key={strategy.value} size="small" style={{ cursor: 'pointer' }}>
                                    <Radio value={strategy.value} style={{ width: '100%' }}>
                                        <div style={{ marginLeft: 8 }}>
                                            <div style={{ fontWeight: 500, marginBottom: 4 }}>
                                                {strategy.label}
                                                {strategy.value === 'adaptive' && (
                                                    <span style={{ 
                                                        fontSize: '11px', 
                                                        color: '#6366f1', 
                                                        marginLeft: 8,
                                                        backgroundColor: '#e0e7ff',
                                                        padding: '2px 6px',
                                                        borderRadius: 4
                                                    }}>
                                                        {intl.formatMessage({ id: 'survey.form.comingSoon' })}
                                                    </span>
                                                )}
                                            </div>
                                            <div style={{ color: '#6b7280', fontSize: '14px' }}>
                                                {strategy.description}
                                            </div>
                                        </div>
                                    </Radio>
                                </Card>
                            ))}
                        </div>
                    </Radio.Group>
                </Form.Item> */}

                <Divider style={{ margin: '32px 0' }} />

                {/* Survey Settings */}
                <Collapse 
                    defaultActiveKey={['2', '3', '4']}
                    ghost
                    style={{ marginBottom: 24 }}
                >
                    {/* Survey Experience - Hidden for now */}
                    {/* <Panel 
                        header={
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <ExperimentOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                                <span style={{ fontWeight: 600, fontSize: '16px' }}>
                                    {intl.formatMessage({ id: 'survey.settings.surveyExperience' })}
                                </span>
                            </div>
                        } 
                        key="1"
                    >
                        <div style={{ padding: '16px 0' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                                <Form.Item label={intl.formatMessage({ id: 'survey.settings.showProgressBar' })}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <Switch
                                            checked={formData.showProgressBar}
                                            onChange={handleShowProgressBarChange}
                                        />
                                        <span style={{ color: '#6b7280', fontSize: '14px' }}>
                                            {intl.formatMessage({ id: 'survey.settings.showProgressBarDesc' })}
                                        </span>
                                    </div>
                                </Form.Item>

                                <Form.Item 
                                    label={intl.formatMessage({ id: 'survey.settings.animationSpeed' })}
                                    validateStatus={errors.animationSpeed ? 'error' : ''}
                                    help={errors.animationSpeed}
                                >
                                    <InputNumber
                                        value={formData.animationSpeed}
                                        onChange={handleAnimationSpeedChange}
                                        min={200}
                                        max={2000}
                                        step={100}
                                        style={{ width: '100%' }}
                                        addonAfter="ms"
                                    />
                                    <div style={{ color: '#6b7280', fontSize: '12px', marginTop: 4 }}>
                                        {intl.formatMessage({ id: 'survey.settings.animationSpeedDesc' })}
                                    </div>
                                </Form.Item>

                                <Form.Item label={intl.formatMessage({ id: 'survey.settings.allowSkip' })}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <Switch
                                            checked={formData.allowSkip}
                                            onChange={handleAllowSkipChange}
                                        />
                                        <span style={{ color: '#6b7280', fontSize: '14px' }}>
                                            {intl.formatMessage({ id: 'survey.settings.allowSkipDesc' })}
                                        </span>
                                    </div>
                                </Form.Item>

                                <Form.Item label={intl.formatMessage({ id: 'survey.settings.allowBackNavigation' })}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <Switch
                                            checked={formData.allowBackNavigation}
                                            onChange={handleAllowBackNavigationChange}
                                        />
                                        <span style={{ color: '#6b7280', fontSize: '14px' }}>
                                            {intl.formatMessage({ id: 'survey.settings.allowBackNavigationDesc' })}
                                        </span>
                                    </div>
                                </Form.Item>

                                <Form.Item label={intl.formatMessage({ id: 'survey.settings.persistProgress' })}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <Switch
                                            checked={formData.persistProgress}
                                            onChange={handlePersistProgressChange}
                                        />
                                        <span style={{ color: '#6b7280', fontSize: '14px' }}>
                                            {intl.formatMessage({ id: 'survey.settings.persistProgressDesc' })}
                                        </span>
                                    </div>
                                </Form.Item>

                                <Form.Item 
                                    label={intl.formatMessage({ id: 'survey.settings.expirationHours' })}
                                    validateStatus={errors.expirationHours ? 'error' : ''}
                                    help={errors.expirationHours}
                                >
                                    <InputNumber
                                        value={formData.expirationHours}
                                        onChange={handleExpirationHoursChange}
                                        min={1}
                                        max={168}
                                        style={{ width: '100%' }}
                                        addonAfter="hours"
                                    />
                                    <div style={{ color: '#6b7280', fontSize: '12px', marginTop: 4 }}>
                                        {intl.formatMessage({ id: 'survey.settings.expirationHoursDesc' })}
                                    </div>
                                </Form.Item>
                            </div>
                        </div>
                    </Panel> */}

                    <Panel 
                        header={
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <CheckCircleOutlined style={{ marginRight: 8, color: '#52c41a' }} />
                                <span style={{ fontWeight: 600, fontSize: '16px' }}>
                                    {intl.formatMessage({ id: 'survey.settings.completionPage' })}
                                </span>
                            </div>
                        } 
                        key="2"
                    >
                        <div style={{ padding: '16px 0' }}>
                            <div style={{ display: 'grid', gap: 16 }}>
                                <Form.Item label={intl.formatMessage({ id: 'survey.settings.completionTitle' })}>
                                    <Input
                                        value={formData.completionTitle}
                                        onChange={handleCompletionTitleChange}
                                        placeholder="Survey Complete!"
                                        size="large"
                                    />
                                </Form.Item>

                                <Form.Item label={intl.formatMessage({ id: 'survey.settings.completionSubtitle' })}>
                                    <TextArea
                                        value={formData.completionSubtitle}
                                        onChange={handleCompletionSubtitleChange}
                                        placeholder="Thank you for taking the time to share your feedback..."
                                        rows={3}
                                        size="large"
                                    />
                                </Form.Item>

                                {/* The reward (SYR-105 phase 2). Same panel treatment as the
                                    two button groups above. Revealed on the thank-you page and
                                    nowhere else: if the offer is the hook in the invitation
                                    email the send selects for deal-seekers and the data goes
                                    soft. Shown to every completer without exception. */}
                                <div style={{
                                    backgroundColor: '#f6f8fa',
                                    border: '1px solid #e1e4e8',
                                    borderRadius: '8px',
                                    padding: '16px'
                                }}>
                                <div style={{ fontWeight: 600, marginBottom: 12 }}>
                                    {intl.formatMessage({ id: 'survey.settings.offerSection' })}
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 170px', gap: 16, alignItems: 'start' }}>
                                    <div style={{ display: 'grid', gap: 16 }}>
                                        <Form.Item
                                            label={intl.formatMessage({ id: 'survey.settings.discountCode' })}
                                            validateStatus={formData.showReward && !formData.discountCode?.trim() ? 'error' : ''}
                                            help={
                                                formData.showReward && !formData.discountCode?.trim()
                                                    ? intl.formatMessage({ id: 'survey.form.validation.discountCodeRequired' })
                                                    : intl.formatMessage({ id: 'survey.settings.discountCodeHelp' })
                                            }
                                        >
                                            <Input
                                                value={formData.discountCode || ''}
                                                onChange={handleDiscountCodeChange}
                                                placeholder="THANKYOU15"
                                                size="large"
                                                maxLength={100}
                                                disabled={!formData.showReward}
                                            />
                                        </Form.Item>

                                        <Form.Item
                                            label={intl.formatMessage({ id: 'survey.settings.offerDescription' })}
                                            validateStatus={formData.showReward && !formData.offerDescription?.trim() ? 'error' : ''}
                                            help={
                                                formData.showReward && !formData.offerDescription?.trim()
                                                    ? intl.formatMessage({ id: 'survey.form.validation.offerDescriptionRequired' })
                                                    : intl.formatMessage({ id: 'survey.settings.offerDescriptionHelp' })
                                            }
                                        >
                                            <Input
                                                value={formData.offerDescription || ''}
                                                onChange={handleOfferDescriptionChange}
                                                placeholder="15% off your next order"
                                                size="large"
                                                maxLength={255}
                                                disabled={!formData.showReward}
                                            />
                                        </Form.Item>

                                        <Form.Item
                                            label={intl.formatMessage({ id: 'survey.settings.offerTerms' })}
                                            help={intl.formatMessage({ id: 'survey.settings.offerTermsHelp' })}
                                        >
                                            <TextArea
                                                value={formData.offerTerms || ''}
                                                onChange={handleOfferTermsChange}
                                                placeholder="Expires 30 days from today. One use per customer."
                                                rows={2}
                                                size="large"
                                                disabled={!formData.showReward}
                                            />
                                        </Form.Item>
                                    </div>

                                    <Form.Item label={intl.formatMessage({ id: 'survey.settings.showReward' })} style={{ paddingTop: 8 }}>
                                        <Switch
                                            checked={formData.showReward}
                                            onChange={handleShowRewardChange}
                                        />
                                    </Form.Item>
                                </div>
                                </div>

                                <div style={{
                                    backgroundColor: '#f6f8fa',
                                    border: '1px solid #e1e4e8',
                                    borderRadius: '8px',
                                    padding: '16px'
                                }}>
                                <div style={{ fontWeight: 600, marginBottom: 12 }}>
                                    {intl.formatMessage({ id: 'survey.settings.primaryButtonGroup' })}
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 170px', gap: 16, alignItems: 'start' }}>
                                    <div style={{ display: 'grid', gap: 16 }}>
                                        <Form.Item
                                            label={intl.formatMessage({ id: 'survey.settings.completionButtonText' })}
                                            validateStatus={formData.showCompletionButton && !formData.completionButtonText?.trim() ? 'error' : ''}
                                            help={formData.showCompletionButton && !formData.completionButtonText?.trim() ? intl.formatMessage({ id: 'survey.form.validation.buttonTextRequired' }) : ''}
                                        >
                                            <Input
                                                value={formData.completionButtonText}
                                                onChange={handleCompletionButtonTextChange}
                                                placeholder="Visit our website"
                                                size="large"
                                                disabled={!formData.showCompletionButton}
                                            />
                                        </Form.Item>

                                        <Form.Item
                                            label={intl.formatMessage({ id: 'survey.settings.completionButtonLink' })}
                                            validateStatus={
                                                (formData.completionButtonLink && !isValidUrl(formData.completionButtonLink)) ||
                                                (formData.showCompletionButton && !formData.completionButtonLink?.trim())
                                                    ? 'error' : ''
                                            }
                                            help={
                                                formData.completionButtonLink && !isValidUrl(formData.completionButtonLink)
                                                    ? intl.formatMessage({ id: 'survey.form.validation.invalidUrl' })
                                                    : formData.showCompletionButton && !formData.completionButtonLink?.trim()
                                                        ? intl.formatMessage({ id: 'survey.form.validation.buttonLinkRequired' })
                                                        : ''
                                            }
                                        >
                                            <Input
                                                value={formData.completionButtonLink}
                                                onChange={handleCompletionButtonLinkChange}
                                                placeholder="https://example.com"
                                                size="large"
                                                disabled={!formData.showCompletionButton}
                                            />
                                        </Form.Item>
                                    </div>

                                    <Form.Item label={intl.formatMessage({ id: 'survey.settings.showCompletionButton' })} style={{ paddingTop: 8 }}>
                                        <Switch
                                            checked={formData.showCompletionButton}
                                            onChange={handleShowCompletionButtonChange}
                                        />
                                    </Form.Item>
                                </div>
                                </div>

                                {/* Second button (SYR-93) — e.g. review CTA shown alongside the shop link */}
                                <div style={{
                                    backgroundColor: '#f6f8fa',
                                    border: '1px solid #e1e4e8',
                                    borderRadius: '8px',
                                    padding: '16px'
                                }}>
                                <div style={{ fontWeight: 600, marginBottom: 12 }}>
                                    {intl.formatMessage({ id: 'survey.settings.secondaryButtonGroup' })}
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 170px', gap: 16, alignItems: 'start' }}>
                                    <div style={{ display: 'grid', gap: 16 }}>
                                        <Form.Item
                                            label={intl.formatMessage({ id: 'survey.settings.completionButtonText' })}
                                            validateStatus={formData.showCompletionSecondaryButton && !formData.completionSecondaryButtonText?.trim() ? 'error' : ''}
                                            help={formData.showCompletionSecondaryButton && !formData.completionSecondaryButtonText?.trim() ? intl.formatMessage({ id: 'survey.form.validation.secondaryButtonTextRequired' }) : ''}
                                        >
                                            <Input
                                                value={formData.completionSecondaryButtonText}
                                                onChange={handleCompletionSecondaryButtonTextChange}
                                                placeholder="Leave us a review"
                                                size="large"
                                                maxLength={80}
                                                disabled={!formData.showCompletionSecondaryButton}
                                            />
                                        </Form.Item>

                                        <Form.Item
                                            label={intl.formatMessage({ id: 'survey.settings.completionButtonLink' })}
                                            validateStatus={
                                                (formData.completionSecondaryButtonLink && !isValidHttpsUrl(formData.completionSecondaryButtonLink)) ||
                                                (formData.showCompletionSecondaryButton && !formData.completionSecondaryButtonLink?.trim())
                                                    ? 'error' : ''
                                            }
                                            help={
                                                formData.completionSecondaryButtonLink && !isValidHttpsUrl(formData.completionSecondaryButtonLink)
                                                    ? intl.formatMessage({ id: 'survey.form.validation.httpsUrlRequired' })
                                                    : formData.showCompletionSecondaryButton && !formData.completionSecondaryButtonLink?.trim()
                                                        ? intl.formatMessage({ id: 'survey.form.validation.secondaryButtonLinkRequired' })
                                                        : intl.formatMessage({ id: 'survey.settings.completionSecondaryButtonLinkHelp' })
                                            }
                                        >
                                            <Input
                                                value={formData.completionSecondaryButtonLink}
                                                onChange={handleCompletionSecondaryButtonLinkChange}
                                                placeholder="https://g.page/r/your-business/review"
                                                size="large"
                                                disabled={!formData.showCompletionSecondaryButton}
                                            />
                                        </Form.Item>
                                    </div>

                                    <Form.Item label={intl.formatMessage({ id: 'survey.settings.showCompletionButton' })} style={{ paddingTop: 8 }}>
                                        <Switch
                                            checked={formData.showCompletionSecondaryButton}
                                            onChange={handleShowCompletionSecondaryButtonChange}
                                        />
                                    </Form.Item>
                                </div>
                                </div>

                                {/* Preview Card. Uses the shared ThankYouPreview so this and
                                    the concierge offer screen show the same page — and the same
                                    page the survey app actually renders. The old hand-built
                                    version had no check circle and square primary-coloured
                                    buttons, so it agreed with neither. */}
                                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
                                    <Card
                                        title={intl.formatMessage({ id: 'survey.settings.preview' })}
                                        style={{ width: '100%' }}
                                    >
                                        <ThankYouPreview
                                            headline={formData.completionTitle}
                                            subtitle={formData.completionSubtitle || null}
                                            theme={formData.theme}
                                            code={formData.showReward ? formData.discountCode || '' : ''}
                                            offer={formData.offerDescription || ''}
                                            terms={formData.offerTerms || ''}
                                            showDestination={Boolean(formData.showCompletionButton)}
                                            showReview={Boolean(formData.showCompletionSecondaryButton)}
                                            destinationLabel={formData.completionButtonText}
                                            reviewLabel={formData.completionSecondaryButtonText || ''}
                                        />
                                    </Card>
                                </div>
                            </div>
                        </div>
                    </Panel>

                    <Panel 
                        header={
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <MailOutlined style={{ marginRight: 8, color: '#722ed1' }} />
                                <span style={{ fontWeight: 600, fontSize: '16px' }}>
                                    {intl.formatMessage({ id: 'survey.settings.emailCollection' })}
                                </span>
                            </div>
                        } 
                        key="3"
                    >
                        <div style={{ padding: '16px 0' }}>
                            <div style={{ display: 'grid', gap: 20 }}>
                                {/* Email Collection Info */}
                                <div style={{ 
                                    backgroundColor: '#f6f8fa', 
                                    border: '1px solid #e1e4e8',
                                    borderRadius: '8px',
                                    padding: '16px'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                        <MailOutlined style={{ color: '#722ed1', fontSize: '16px' }} />
                                        <span style={{ fontWeight: 500, fontSize: '16px' }}>
                                            {intl.formatMessage({ id: 'survey.settings.automaticEmailCollection' })}
                                        </span>
                                    </div>
                                    <div style={{ color: '#6b7280', fontSize: '14px', lineHeight: '1.5' }}>
                                        {intl.formatMessage({ id: 'survey.settings.automaticEmailCollectionDesc' })}
                                    </div>
                                </div>

                                {/* Participant Identification Info */}
                                <div style={{ 
                                    backgroundColor: '#fef3cd', 
                                    border: '1px solid #f9e2af',
                                    borderRadius: '8px',
                                    padding: '16px'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                        <InfoCircleOutlined style={{ color: '#b45309', fontSize: '16px' }} />
                                        <span style={{ fontWeight: 500, fontSize: '16px', color: '#b45309' }}>
                                            {intl.formatMessage({ id: 'survey.settings.participantIdentification' })}
                                        </span>
                                    </div>
                                    <div style={{ color: '#b45309', fontSize: '14px', lineHeight: '1.5' }}>
                                        {intl.formatMessage({ id: 'survey.settings.participantIdentificationDesc' })}
                                    </div>
                                </div>

                                {/* Email Placeholder Setting */}
                                <Form.Item 
                                    label={intl.formatMessage({ id: 'survey.settings.emailPlaceholder' })}
                                    validateStatus={errors.emailPlaceholder ? 'error' : ''}
                                    help={errors.emailPlaceholder}
                                >
                                    <Input
                                        value={formData.emailPlaceholder}
                                        onChange={handleEmailPlaceholderChange}
                                        placeholder="Enter your email address"
                                        size="large"
                                        maxLength={255}
                                    />
                                    <div style={{ 
                                        color: '#6b7280', 
                                        fontSize: '12px', 
                                        marginTop: 4 
                                    }}>
                                        {intl.formatMessage({ id: 'survey.settings.emailPlaceholderDesc' })}
                                    </div>
                                </Form.Item>

                                {/* Tip */}
                                <div style={{ 
                                    backgroundColor: '#f0f9ff', 
                                    border: '1px solid #e0f2fe',
                                    borderRadius: '8px',
                                    padding: '12px 16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8
                                }}>
                                    <InfoCircleOutlined style={{ color: '#0ea5e9', fontSize: '16px' }} />
                                    <span style={{ color: '#0c4a6e', fontSize: '14px' }}>
                                        💡 {intl.formatMessage({ id: 'survey.settings.emailTip' })}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </Panel>

                    <Panel 
                        header={
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <InfoCircleOutlined style={{ marginRight: 8, color: '#fa8c16' }} />
                                <span style={{ fontWeight: 600, fontSize: '16px' }}>
                                    {intl.formatMessage({ id: 'survey.settings.npsCollection' })}
                                </span>
                            </div>
                        } 
                        key="4"
                    >
                        <div style={{ padding: '16px 0' }}>
                            <div style={{ display: 'grid', gap: 20 }}>
                                {/* NPS Required Setting */}
                                <Form.Item>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                        <Switch
                                            checked={formData.npsRequired}
                                            onChange={handleNpsRequiredChange}
                                        />
                                        <span style={{ fontWeight: 500 }}>
                                            {intl.formatMessage({ id: 'survey.settings.npsRequired' })}
                                        </span>
                                    </div>
                                    <div style={{ color: '#6b7280', fontSize: '14px', marginLeft: 28 }}>
                                        {intl.formatMessage({ id: 'survey.settings.npsRequiredDesc' })}
                                    </div>
                                </Form.Item>

                                {/* Info Card */}
                                <div style={{
                                    backgroundColor: '#fff7e6',
                                    border: '1px solid #ffd591',
                                    borderRadius: '8px',
                                    padding: '12px 16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8
                                }}>
                                    <WarningOutlined style={{ color: '#fa8c16', fontSize: '16px' }} />
                                    <span style={{ color: '#ad6800', fontSize: '14px' }}>
                                        {intl.formatMessage({ id: 'survey.settings.npsInfo' })}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </Panel>

                    {finalQuestionsData?.data && finalQuestionsData.data.length > 0 && (
                        <Panel
                            header={
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <MailOutlined style={{ marginRight: 8, color: '#52c41a' }} />
                                    <span style={{ fontWeight: 600, fontSize: '16px' }}>
                                        Final Question (Optional)
                                    </span>
                                </div>
                            }
                            key="5"
                        >
                            <div style={{ padding: '16px 0' }}>
                                <div style={{ display: 'grid', gap: 20 }}>
                                    {/* Info */}
                                    <div style={{
                                        backgroundColor: '#f6ffed',
                                        border: '1px solid #b7eb8f',
                                        borderRadius: '8px',
                                        padding: '12px 16px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8
                                    }}>
                                        <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '16px' }} />
                                        <span style={{ color: '#389e0d', fontSize: '14px' }}>
                                            Add an optional open-ended question at the end of your survey to gather additional feedback.
                                        </span>
                                    </div>

                                    {/* Final Question Selector */}
                                    <Form.Item
                                        label="Select Final Question"
                                        help="Choose an optional question to display after the standard survey questions"
                                    >
                                        <Select
                                            value={formData.finalQuestionId}
                                            onChange={handleFinalQuestionChange}
                                            placeholder="None (no final question)"
                                            size="large"
                                            allowClear
                                            style={{ width: '100%' }}
                                        >
                                            {finalQuestionsData?.data?.map((question) => (
                                                <Select.Option key={question.id} value={question.id}>
                                                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        {question.useAsDefault && (
                                                            <StarFilled style={{ color: '#faad14', fontSize: 12 }} />
                                                        )}
                                                        {question.text}
                                                        {question.useAsDefault && (
                                                            <span style={{ color: '#faad14', fontSize: 11, marginLeft: 'auto' }}>
                                                                (Default)
                                                            </span>
                                                        )}
                                                    </span>
                                                </Select.Option>
                                            ))}
                                        </Select>
                                        <div style={{
                                            color: '#6b7280',
                                            fontSize: '12px',
                                            marginTop: 4
                                        }}>
                                            Respondents can skip this question if they prefer not to answer.
                                        </div>
                                    </Form.Item>
                                </div>
                            </div>
                        </Panel>
                    )}
                </Collapse>
            </Form>
        </div>
    );
};

export default ConfigurationStep;